import config from '../config';
import { normalizeOnboarding, normalizePreferredExperience } from '../utils/onboardingNavigation';

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

function onboardingError(message, code, status) {
  const error = new Error(message);
  error.code = code;
  if (status !== undefined) error.status = status;
  return error;
}

// A successful save must actually return the server's persisted record. Do not
// normalize malformed responses into an apparently successful legacy record.
function validResponse(value) {
  return isRecord(value)
    && hasOwn(value, 'preferred_experience')
    && hasOwn(value, 'onboarding_completed')
    && (value.preferred_experience === null || ['chat', 'remote', 'later'].includes(value.preferred_experience))
    && typeof value.onboarding_completed === 'boolean';
}

async function requestOnboarding(method, patch, { signal, requireEndpoint = false } = {}) {
  const response = await fetch(`${config.apiV2Url.replace(/\/+$/, '')}/onboarding`, {
    method,
    credentials: 'include',
    ...(signal ? { signal } : {}),
    ...(method === 'PUT' ? {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    } : {}),
  });
  // Explicitly unavailable, not persisted success. The caller can offer legacy
  // navigation but must not claim a choice was saved. Never cache across users.
  if ([404, 405, 501].includes(response.status)) {
    if (requireEndpoint) {
      throw onboardingError(`Onboarding endpoint unavailable (${response.status})`, 'endpoint_unavailable', response.status);
    }
    return { available: false, onboarding: null };
  }
  if (!response.ok) {
    // Do not surface arbitrary server bodies containing validation inputs/PII.
    throw onboardingError(`Onboarding request failed (${response.status})`, 'request_failed', response.status);
  }
  let payload;
  try {
    payload = await response.json();
  } catch (_) {
    throw onboardingError('Invalid onboarding response', 'invalid_response', response.status);
  }
  if (!validResponse(payload)) {
    throw onboardingError('Invalid onboarding response', 'invalid_response', response.status);
  }
  return { available: true, onboarding: normalizeOnboarding(payload) };
}

/** GET current authenticated account; options supports AbortSignal. */
export function getOnboarding(options = {}) {
  return requestOnboarding('GET', undefined, options);
}

/** PUT a nonempty partial update. Only the two contract fields are sent.
 * Unknown fields (email, draft, credentials, etc.) are discarded, not stored.
 * Invalid known fields reject before any request. null clears the preference.
 */
export async function updateOnboarding(patch, options = {}) {
  if (!isRecord(patch)) throw onboardingError('Invalid onboarding update', 'invalid_update');
  const body = {};
  if (hasOwn(patch, 'preferred_experience')) {
    const preference = normalizePreferredExperience(patch.preferred_experience);
    if (patch.preferred_experience !== null && preference === null) {
      throw onboardingError('Invalid preferred experience', 'invalid_update');
    }
    body.preferred_experience = preference;
  }
  if (hasOwn(patch, 'onboarding_completed')) {
    if (typeof patch.onboarding_completed !== 'boolean') {
      throw onboardingError('Invalid onboarding completion', 'invalid_update');
    }
    body.onboarding_completed = patch.onboarding_completed;
  }
  if (!Object.keys(body).length) throw onboardingError('Empty onboarding update', 'invalid_update');
  return requestOnboarding('PUT', body, options);
}

/** Integration API: same success envelope, but unavailable endpoints reject
 * with code=endpoint_unavailable and status=404/405/501 for caller fallback.
 * Authentication/HTTP errors also carry status. No default record is persisted.
 */
export function getOnboardingPreferences(options = {}) {
  return getOnboarding({ ...options, requireEndpoint: true });
}

export function updateOnboardingPreferences(patch, options = {}) {
  return updateOnboarding(patch, { ...options, requireEndpoint: true });
}
